import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Fab,
  Grow,
  IconButton,
  styled,
  useTheme,
} from "@mui/material";
import { Close, ListAlt } from "@mui/icons-material";
import { ControlOptions } from "leaflet";

import { useTranslation } from "react-i18next";

import { useSelection } from "hooks/selectionHooks";

import { LayerProperties } from "types";

import { MapControlBase } from "./MapControlBase";
import { Legend } from "../legend/Legend";
import { cycleState } from "../../utils/ArrayUtils";

interface Props extends ControlOptions {
  config: string;
  layerProperties: LayerProperties[];
}

type TransitionStates = "closed" | "opening" | "open" | "closing";

export function LegendControl(props: Props) {
  const { config, layerProperties, ...controlProps } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const states: TransitionStates[] = ["closed", "opening", "open", "closing"];
  const [currentState, setCurrentState] = useState<TransitionStates>("closed");
  const { data: selection } = useSelection(config, layerProperties);

  function onClickCycle() {
    const nextState = cycleState<TransitionStates>(states, currentState);
    setCurrentState(nextState);
    setTimeout(
      setCurrentState,
      theme.transitions.duration.standard,
      cycleState<TransitionStates>(states, nextState)
    );
  }

  return (
    <MapControlBase {...controlProps}>
      {selection && selection.indicators.length > 0 && (
        <>
          {["opening", "closed"].includes(currentState) && (
            <Grow
              in={["closed"].includes(currentState)}
              unmountOnExit
              appear={true}
              exit={true}
            >
              <Fab
                className={currentState ? "active" : "inactive"}
                size="small"
                variant="circular"
                onClick={onClickCycle}
              >
                <ListAlt />
              </Fab>
            </Grow>
          )}
          {["closing", "open"].includes(currentState) && (
            <Grow
              in={["open"].includes(currentState)}
              unmountOnExit
              appear={true}
              exit={true}
            >
              <LegendCard>
                <CardHeader
                  subheader={t("label.legend")}
                  action={
                    <IconButton onClick={onClickCycle}>
                      <Close />
                    </IconButton>
                  }
                />
                <CardContent>
                  <Legend
                    selection={selection.indicators}
                    layerProperties={layerProperties}
                    config={config}
                  />
                </CardContent>
              </LegendCard>
            </Grow>
          )}
        </>
      )}
    </MapControlBase>
  );
}

const LegendCard = styled(Card)`
  transform-origin: 0 100% 0;

  .legend-item + .legend-item {
    margin: ${({ theme }) => theme.spacing(1, 0)};
  }

  .MuiCardContent-root {
    background-color: ${({ theme }) => theme.palette.background.default};
    max-height: 76vh;
    overflow-y: auto;
  }
`;
